/**
 * Customer Accounts Redux Slice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  CustomerAccount,
  CustomerAccountStats,
  CustomerAccountsFilter,
  CustomerAccountsState,
  PaginatedCustomerAccounts,
  UpdateAccountPayload,
  ChangeAccountStatusPayload,
  UpdateAllowedCitiesPayload,
} from '../domain/types'

function computeStats(items: CustomerAccount[], total = items.length): CustomerAccountStats {
  return {
    total,
    active: items.filter((account) => account.accountStatus === 'active').length,
    suspended: items.filter((account) => account.accountStatus === 'suspended').length,
    blocked: items.filter((account) => account.accountStatus === 'blocked').length,
    totalCreditLimit: {
      amount: items.reduce((sum, account) => sum + (account.creditLimit ?? 0), 0),
      currency: 'SAR',
    },
    totalOutstanding: {
      amount: items.reduce((sum, account) => sum + (account.currentBalance ?? 0), 0),
      currency: 'SAR',
    },
    totalOverdue: {
      amount: items.reduce((sum, account) => sum + (account.overdueAmount ?? 0), 0),
      currency: 'SAR',
    },
  }
}

const initialState: CustomerAccountsState = {
  items: [],
  selectedAccount: null,
  stats: {
    total: 0,
    active: 0,
    suspended: 0,
    blocked: 0,
    totalCreditLimit: { amount: 0, currency: 'SAR' },
    totalOutstanding: { amount: 0, currency: 'SAR' },
    totalOverdue: { amount: 0, currency: 'SAR' },
  },
  filter: {
    status: 'all',
    paymentTerms: 'all',
    channel: 'all',
    tenantId: 'all',
    page: 1,
    pageSize: 10,
  },
  pagination: {
    page: 1,
    pageSize: 10,
    pageCount: 1,
    total: 0,
  },
  loading: false,
  error: null,
}

export const customerAccountsSlice = createSlice({
  name: 'customerAccounts',
  initialState,
  reducers: {
    // Fetch actions
    fetchAccounts: (state, _action: PayloadAction<CustomerAccountsFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchAccountsSuccess: (state, action: PayloadAction<PaginatedCustomerAccounts>) => {
      state.items = action.payload.data
      state.pagination = action.payload.pagination
      state.stats = computeStats(action.payload.data, action.payload.pagination.total)
      state.loading = false
      state.error = null
    },
    fetchAccountsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Selection
    selectAccount: (state, action: PayloadAction<CustomerAccount | null>) => {
      state.selectedAccount = action.payload
    },

    // Filter
    setFilter: (state, action: PayloadAction<Partial<CustomerAccountsFilter>>) => {
      state.filter = { ...state.filter, ...action.payload }
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.filter.page = action.payload
      state.pagination.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter.pageSize = action.payload
      state.filter.page = 1
      state.pagination.pageSize = action.payload
      state.pagination.page = 1
    },

    // Stats
    setStats: (state, action: PayloadAction<CustomerAccountStats>) => {
      state.stats = action.payload
    },

    // Update account
    updateAccount: (state, _action: PayloadAction<UpdateAccountPayload>) => {
      state.loading = true
    },
    updateAccountSuccess: (state, action: PayloadAction<CustomerAccount>) => {
      const index = state.items.findIndex((a) => a.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      if (state.selectedAccount?.id === action.payload.id) {
        state.selectedAccount = action.payload
      }
      state.stats = computeStats(state.items, state.pagination.total)
      state.loading = false
    },
    updateAccountFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Change status
    changeAccountStatus: (state, _action: PayloadAction<ChangeAccountStatusPayload>) => {
      state.loading = true
    },
    changeAccountStatusSuccess: (state, action: PayloadAction<CustomerAccount>) => {
      const index = state.items.findIndex((a) => a.id === action.payload.id)
      if (index !== -1) {
        const oldStatus = state.items[index].accountStatus
        state.items[index] = action.payload
        const newStatus = action.payload.accountStatus
        if (oldStatus !== newStatus) {
          state.stats[oldStatus as keyof Pick<CustomerAccountStats, 'active' | 'suspended' | 'blocked'>] -= 1
          state.stats[newStatus as keyof Pick<CustomerAccountStats, 'active' | 'suspended' | 'blocked'>] += 1
        }
      }
      if (state.selectedAccount?.id === action.payload.id) {
        state.selectedAccount = action.payload
      }
      state.stats = computeStats(state.items, state.pagination.total)
      state.loading = false
    },
    changeAccountStatusFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Update allowed cities
    updateAllowedCities: (state, _action: PayloadAction<UpdateAllowedCitiesPayload>) => {
      state.loading = true
    },
    updateAllowedCitiesSuccess: (state, action: PayloadAction<CustomerAccount>) => {
      const index = state.items.findIndex((a) => a.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      if (state.selectedAccount?.id === action.payload.id) {
        state.selectedAccount = action.payload
      }
      state.stats = computeStats(state.items, state.pagination.total)
      state.loading = false
    },
    updateAllowedCitiesFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Clear
    clearAccounts: (state) => {
      state.items = []
      state.selectedAccount = null
      state.pagination = {
        page: 1,
        pageSize: 10,
        pageCount: 1,
        total: 0,
      }
      state.error = null
    },
  },
})

export const customerAccountsActions = customerAccountsSlice.actions
export const customerAccountsReducer = customerAccountsSlice.reducer
